import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearDemoSession, isDemoMode } from "../utils/demoAuth";
import { resetDemoData } from "../utils/demoStorage";

const pageSubtitles = [
  {
    path: "/settings",
    subtitle: "Manage AI settings, preferences, and workspace controls.",
  },
  {
    path: "/expenses",
    subtitle: "Track, scan, and organize your spending in one place.",
  },
  {
    path: "/income",
    subtitle: "Manage salary, business, and recurring income sources.",
  },
  {
    path: "/budget",
    subtitle: "Set category limits and monitor budget risk.",
  },
  {
    path: "/reports",
    subtitle: "Review income, expenses, trends, and exports.",
  },
];

const getTopbarSubtitle = (pathname) =>
  pageSubtitles.find((item) => pathname.includes(item.path))?.subtitle ||
  "AI-powered personal finance intelligence platform.";

function Topbar({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const demoMode =
    localStorage.getItem("isDemoMode") === "true" || isDemoMode();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetToast, setResetToast] = useState("");

  useEffect(() => {
    if (!resetToast) return undefined;

    const timer = window.setTimeout(() => setResetToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [resetToast]);

  const handleLogout = () => {
    if (demoMode) {
      clearDemoSession();
      navigate("/");
      return;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleResetDemoData = () => {
    const selectedMonth = Number(localStorage.getItem("selectedMonth"));
    const selectedYear = Number(localStorage.getItem("selectedYear"));
    const now = new Date();

    resetDemoData(
      selectedMonth >= 1 && selectedMonth <= 12 ? selectedMonth : now.getMonth() + 1,
      selectedYear >= 1900 ? selectedYear : now.getFullYear()
    );
    setShowResetConfirm(false);
    setResetToast("Demo data restored successfully");
  };

  return (
    <header className="topbar">
      <div className="min-w-0 max-w-full">
        <h1 className="max-w-full !text-2xl lg:!text-3xl xl:!text-4xl leading-tight md:whitespace-nowrap md:truncate">
          FinoraAI Command Center
        </h1>
        <p className="max-w-full md:truncate">{getTopbarSubtitle(location.pathname)}</p>
      </div>

      <div className="topbar-actions">
        {children}

        {demoMode && <button className="demo-btn">Demo Mode - Sample Data</button>}

        {demoMode && (
          <button
            type="button"
            className="reset-demo-btn"
            onClick={() => setShowResetConfirm(true)}
            title="Restore clean demo sample data"
          >
            <span aria-hidden="true">↻</span>
            Reset Demo Data
          </button>
        )}

        <div className="user-pill">{demoMode ? "DM" : "FA"}</div>

        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {showResetConfirm && (
        <div className="demo-reset-overlay" role="presentation">
          <div className="demo-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="demo-reset-title">
            <div className="demo-reset-icon" aria-hidden="true">↻</div>
            <h2 id="demo-reset-title">Reset Demo Environment?</h2>
            <p>
              This will restore all demo expenses, income, budgets, reports, OCR data,
              and AI demo activity back to the original sample state.
            </p>
            <div className="demo-reset-actions">
              <button
                type="button"
                className="demo-reset-cancel"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="demo-reset-confirm"
                onClick={handleResetDemoData}
              >
                Reset Demo
              </button>
            </div>
          </div>
        </div>
      )}

      {resetToast && <div className="demo-reset-toast">{resetToast}</div>}
    </header>
  );
}

export default Topbar;
